import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from 'src/app/authentication/services/auth.service';
import { AccountContextService } from 'src/app/authentication/services/account-context.service';
import { UsersService } from 'src/app/configurations/services/users.service';
import { PermissionsService } from 'src/app/permissions/services/permissions.service';
import { RouterLink } from '../models/router-link.model';
import { catchError, filter, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { EMPTY, forkJoin, Subject } from 'rxjs';

const productsToShowPermissions = [ 
  { name: "Plugin Manufacturer Design Auditor", library: "3179"}
];

@Component({
  selector: 'navmenu',
  templateUrl: './navmenu.component.html',
  styleUrls: ['./navmenu.component.scss']
})

export class NavmenuComponent implements OnInit, OnDestroy {
  loading: boolean = false;
  shouldShowSideNav: boolean = false;
  routerLinks: RouterLink[] = this.getDefaultRouterLinks();
  private destroy$ = new Subject<void>();

  constructor(
    private usersService: UsersService,
    private authService: AuthService,
    private accountContext: AccountContextService,
    private permissionService: PermissionsService) { }

  ngOnInit() {
    this.authService.authReady$
      .pipe(
        filter(ready => ready),
        take(1),
        switchMap(() => {
          if (!this.authService.isAuthenticated()) {
            return EMPTY;
          }

          this.checkAdmin();

          return this.accountContext.accountChanged$
            .pipe(startWith(this.accountContext.getSelectedAccountId()));
        }),
        takeUntil(this.destroy$))
      .subscribe(() => {
        this.initializeNavigation();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeNavigation() {
    this.routerLinks = this.getDefaultRouterLinks();

    const account = this.accountContext.getSelectedAccountId();
    if (!account) {
      return;
    }

    forkJoin([
      this.permissionService.getProductsLicenseOwnership(account),
      this.permissionService.getLicenseProducts(account)
    ])
      .pipe(
        takeUntil(this.accountContext.accountChanged$),
        takeUntil(this.destroy$),
        catchError(() => EMPTY))
      .subscribe(([isProductOwnership, licenseProducts]) => {
        const hasManufacturerAuditor = licenseProducts
          .some(product => productsToShowPermissions.some(x => x.library == product.library));

        if (isProductOwnership || hasManufacturerAuditor) {
          this.showPermissionButton(true);
        }
      });
  }

  private checkAdmin(): void {
    this.usersService.checkIsAdmin()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => {
          if (err.status === 401) {
            this.authService.login();
          }
        }
      });
  }

  showPermissionButton(hasPermission) {
    if (hasPermission) {
      if (this.routerLinks.some(routerLink => routerLink.route === "/permissions")) {
        return;
      }

      let permissionLink = {
        route: "/permissions",
        routerLinkActiveOptions: {
          exact: false
        },
        iconName: "account_circle"
      }

      let configurationLink = {
        route: "/configurations",
        routerLinkActiveOptions: {
          exact: false
        },
        iconName: "settings"
      }

      this.routerLinks[2] = permissionLink;
      this.routerLinks = [...this.routerLinks, configurationLink]
    }
  }

  public showPermissions(): void {
    let permissionItem =
    {
      route: "/permissions",
      routerLinkActiveOptions: {
        exact: false
      },
      iconName: "account_circle"
    };
    this.routerLinks.splice(2, 0, permissionItem);
  }

  public showAllCharts(): void {
    let allChartsItem =
    {
      route: "/all-charts",
      routerLinkActiveOptions: {
        exact: false
      },
      iconName: "bar_chart"
    };
    this.routerLinks.splice(2, 0, allChartsItem);
  }

  private getDefaultRouterLinks(): RouterLink[] {
    return [
      {
        route: "/projects",
        routerLinkActiveOptions: {
          exact: true
        },
        iconName: "work"
      },
      {
        route: "/all-charts",
        routerLinkActiveOptions: {
          exact: false
        },
        iconName: "bar_chart"
      },
      {
        route: "/configurations",
        routerLinkActiveOptions: {
          exact: false
        },
        iconName: "settings"
      },
      {
        route: "/account-configuration",
        routerLinkActiveOptions: {
          exact: false
        },
        iconName: "settings_applications"
      },
    ];
  }
}
